/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  StatusBar,
  FlatList,
  Image,
  Linking,
  Alert,
  RefreshControl,
  ActivityIndicator,
  Share,
  Dimensions,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  MaterialCommunityIcons,
  Ionicons,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';

const { width } = Dimensions.get('window');

const CareerGuidance = () => {
  const navigation = useNavigation();
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('resources');
  const [savedArticles, setSavedArticles] = useState([]);
  const [completedCourses, setCompletedCourses] = useState([]);
  
  const fieldsListRef = useRef(null);
  const mainListRef = useRef(null);

  const tabs = [
    { id: 'resources', label: 'Resources', icon: 'book-open' },
    { id: 'courses', label: 'Courses', icon: 'school' },
    { id: 'tools', label: 'Tools', icon: 'toolbox' },
    { id: 'community', label: 'Community', icon: 'account-group' },
  ];

  const engineeringFields = [
    { 
      id: 'software', 
      name: 'Software Engineering', 
      icon: 'code-braces',
      color: '#6366f1',
      description: 'Build software systems and applications',
      demand: 'Very High',
      avgSalary: '$110,000',
      growth: '22% (2020-2030)'
    },
    { 
      id: 'mechanical', 
      name: 'Mechanical Engineering', 
      icon: 'cog',
      color: '#f97316',
      description: 'Design mechanical systems and devices',
      demand: 'High',
      avgSalary: '$95,000',
      growth: '7% (2020-2030)'
    },
    { 
      id: 'civil', 
      name: 'Civil Engineering', 
      icon: 'bridge',
      color: '#3b82f6',
      description: 'Design infrastructure and construction projects',
      demand: 'High',
      avgSalary: '$88,000',
      growth: '8% (2020-2030)'
    },
    { 
      id: 'electrical', 
      name: 'Electrical Engineering', 
      icon: 'flash',
      color: '#eab308',
      description: 'Work with electrical systems and electronics',
      demand: 'High',
      avgSalary: '$102,000',
      growth: '7% (2020-2030)'
    },
    { 
      id: 'chemical', 
      name: 'Chemical Engineering', 
      icon: 'flask',
      color: '#10b981',
      description: 'Design chemical manufacturing processes',
      demand: 'Medium',
      avgSalary: '$108,000',
      growth: '9% (2020-2030)'
    },
    { 
      id: 'aerospace', 
      name: 'Aerospace Engineering', 
      icon: 'airplane',
      color: '#8b5cf6',
      description: 'Design aircraft and spacecraft',
      demand: 'Medium',
      avgSalary: '$118,000',
      growth: '8% (2020-2030)'
    },
    { 
      id: 'biomedical', 
      name: 'Biomedical Engineering', 
      icon: 'heart-pulse',
      color: '#ec4899',
      description: 'Combine engineering with medical sciences',
      demand: 'High',
      avgSalary: '$92,000',
      growth: '10% (2020-2030)'
    },
    { 
      id: 'data', 
      name: 'Data Science', 
      icon: 'chart-bar',
      color: '#6366f1',
      description: 'Analyze and interpret complex data',
      demand: 'Very High',
      avgSalary: '$120,000',
      growth: '31% (2020-2030)'
    },
  ];

  const learningResources = [
    {
      id: '1',
      title: 'Engineering Career Paths Guide',
      type: 'guide',
      icon: 'map',
      duration: '15 min read',
      category: 'Career Planning',
      description: 'Comprehensive guide to different engineering career paths and progression',
      url: 'https://www.engineering.com/career-paths',
      saved: false,
    },
    {
      id: '2',
      title: 'Resume Building for Engineers',
      type: 'article',
      icon: 'file-document',
      duration: '10 min read',
      category: 'Job Search',
      description: 'How to create an effective engineering resume with examples',
      url: 'https://www.engineering.com/resume-tips',
      saved: false,
    },
    {
      id: '3',
      title: 'Technical Interview Preparation',
      type: 'video',
      icon: 'video',
      duration: '45 min',
      category: 'Interview Prep',
      description: 'Master technical interviews with common engineering questions',
      url: 'https://www.youtube.com/watch?v=engineering-interview',
      saved: false,
    },
    {
      id: '4',
      title: 'Salary Negotiation Strategies',
      type: 'article',
      icon: 'cash',
      duration: '12 min read',
      category: 'Career Growth',
      description: 'How to negotiate your engineering salary effectively',
      url: 'https://www.engineering.com/salary-negotiation',
      saved: false,
    },
    {
      id: '5',
      title: 'Engineering Portfolio Building',
      type: 'guide',
      icon: 'briefcase',
      duration: '20 min read',
      category: 'Portfolio',
      description: 'Create a standout engineering portfolio with project examples',
      url: 'https://www.engineering.com/portfolio-guide',
      saved: false,
    },
    {
      id: '6',
      title: 'Networking for Engineers',
      type: 'article',
      icon: 'account-network',
      duration: '8 min read',
      category: 'Networking',
      description: 'Effective networking strategies for engineering professionals',
      url: 'https://www.engineering.com/networking',
      saved: false,
    },
  ];

  const onlineCourses = [
    {
      id: 'C1',
      title: 'Modern Software Engineering',
      platform: 'Coursera',
      provider: 'Google',
      duration: '4 months',
      level: 'Intermediate',
      rating: 4.8,
      students: '50K+',
      free: false,
      price: '$49/month',
      certificate: true,
      url: 'https://www.coursera.org/software-engineering',
      completed: false,
    },
    {
      id: 'C2',
      title: 'Mechanical Design Fundamentals',
      platform: 'edX',
      provider: 'MIT',
      duration: '12 weeks',
      level: 'Beginner',
      rating: 4.7,
      students: '35K+',
      free: true,
      price: 'Free',
      certificate: true,
      url: 'https://www.edx.org/mechanical-design',
      completed: false,
    },
    {
      id: 'C3',
      title: 'Civil Engineering Principles',
      platform: 'Udemy',
      provider: 'Stanford',
      duration: '15 hours',
      level: 'Beginner',
      rating: 4.6,
      students: '28K+',
      free: false,
      price: '$94.99',
      certificate: true,
      url: 'https://www.udemy.com/civil-engineering',
      completed: false,
    },
    {
      id: 'C4',
      title: 'Electrical Circuits Masterclass',
      platform: 'Khan Academy',
      provider: 'Khan Academy',
      duration: 'Self-paced',
      level: 'All Levels',
      rating: 4.9,
      students: '100K+',
      free: true,
      price: 'Free',
      certificate: false,
      url: 'https://www.khanacademy.org/electrical',
      completed: false,
    },
    {
      id: 'C5',
      title: 'Data Science for Engineers',
      platform: 'Coursera',
      provider: 'IBM',
      duration: '6 months',
      level: 'Advanced',
      rating: 4.8,
      students: '45K+',
      free: false,
      price: '$39/month',
      certificate: true,
      url: 'https://www.coursera.org/data-science-engineering',
      completed: false,
    },
    {
      id: 'C6',
      title: 'Project Management for Engineers',
      platform: 'LinkedIn Learning',
      provider: 'PMI',
      duration: '8 hours',
      level: 'Intermediate',
      rating: 4.5,
      students: '22K+',
      free: false,
      price: '$29.99',
      certificate: true,
      url: 'https://www.linkedin.com/learning/project-management-engineers',
      completed: false,
    },
  ];

  const careerTools = [
    {
      id: 'T1',
      title: 'Salary Calculator',
      icon: 'calculator',
      description: 'Calculate engineering salaries by location and experience',
      action: 'Calculate',
      screen: 'SalaryCalculator',
    },
    {
      id: 'T2',
      title: 'Resume Builder',
      icon: 'file-edit',
      description: 'Create professional engineering resumes',
      action: 'Build',
      screen: 'ResumeBuilder',
    },
    {
      id: 'T3',
      title: 'Cover Letter Generator',
      icon: 'email-edit',
      description: 'Generate customized cover letters',
      action: 'Generate',
      screen: 'CoverLetterGenerator',
    },
    {
      id: 'T4',
      title: 'Interview Simulator',
      icon: 'microphone',
      description: 'Practice technical interview questions',
      action: 'Practice',
      screen: 'InterviewSimulator',
    },
    {
      id: 'T5',
      title: 'Portfolio Creator',
      icon: 'briefcase-edit',
      description: 'Build your engineering portfolio',
      action: 'Create',
      screen: 'PortfolioCreator',
    },
    {
      id: 'T6',
      title: 'Skill Assessment',
      icon: 'chart-box',
      description: 'Assess your engineering skills',
      action: 'Assess',
      screen: 'SkillAssessment',
    },
  ];

  const communityResources = [
    {
      id: 'COM1',
      title: 'Engineering Reddit',
      icon: 'reddit',
      type: 'Forum',
      members: '2.5M+',
      description: 'Largest engineering community on Reddit',
      url: 'https://www.reddit.com/r/engineering/',
    },
    {
      id: 'COM2',
      title: 'Stack Overflow',
      icon: 'stack-overflow',
      type: 'Q&A',
      members: '10M+',
      description: 'Programming and engineering Q&A',
      url: 'https://stackoverflow.com/',
    },
    {
      id: 'COM3',
      title: 'GitHub Engineering',
      icon: 'github',
      type: 'Code',
      members: '100M+',
      description: 'Open source engineering projects',
      url: 'https://github.com/topics/engineering',
    },
    {
      id: 'COM4',
      title: 'LinkedIn Engineering Groups',
      icon: 'linkedin',
      type: 'Professional',
      members: '5M+',
      description: 'Professional engineering networks',
      url: 'https://www.linkedin.com/groups/engineering',
    },
    {
      id: 'COM5',
      title: 'Engineering Blogs',
      icon: 'rss',
      type: 'Blogs',
      members: '-',
      description: 'Top engineering blogs and publications',
      url: 'https://engineeringblogs.xyz',
    },
    {
      id: 'COM6',
      title: 'Meetup Engineering',
      icon: 'calendar',
      type: 'Events',
      members: '1M+',
      description: 'Engineering events and meetups',
      url: 'https://www.meetup.com/topics/engineering/',
    },
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [darkModePref, saved, completed] = await Promise.all([
        AsyncStorage.getItem('darkMode'),
        AsyncStorage.getItem('savedArticles'),
        AsyncStorage.getItem('completedCourses'),
      ]);
      
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
      
      if (saved) {
        setSavedArticles(JSON.parse(saved));
      }
      
      if (completed) {
        setCompletedCourses(JSON.parse(completed));
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadInitialData();
    setRefreshing(false);
  };

  const toggleSaveArticle = async (articleId) => {
    try {
      let newSaved;
      if (savedArticles.includes(articleId)) {
        newSaved = savedArticles.filter(id => id !== articleId);
      } else {
        newSaved = [...savedArticles, articleId];
      }
      
      setSavedArticles(newSaved);
      await AsyncStorage.setItem('savedArticles', JSON.stringify(newSaved));
    } catch (error) {
      console.error('Error toggling save:', error);
    }
  };

  const markCourseComplete = async (courseId) => {
    try {
      let newCompleted;
      if (completedCourses.includes(courseId)) {
        newCompleted = completedCourses.filter(id => id !== courseId);
      } else {
        newCompleted = [...completedCourses, courseId];
      }
      
      setCompletedCourses(newCompleted);
      await AsyncStorage.setItem('completedCourses', JSON.stringify(newCompleted));
    } catch (error) {
      console.error('Error marking course complete:', error);
    }
  };

  const openLink = async (url) => {
    try {
      await Linking.openURL(url);
    } catch (error) {
      console.error('Failed to open link:', error);
      Alert.alert('Error', 'Could not open the link');
    }
  };

  const shareResource = async (resource) => {
    try {
      await Share.share({
        title: resource.title,
        message: `Check out this engineering resource: ${resource.title}\n\n${resource.description}\n\n${resource.url}`,
        url: resource.url,
      });
    } catch (error) {
      console.error('Error sharing resource:', error);
    }
  };

  const renderHeader = () => (
    <LinearGradient
      colors={darkMode ? ['#2d3748', '#4a5568'] : ['#667eea', '#764ba2']}
      style={styles.header}
    >
      <TouchableOpacity 
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <MaterialCommunityIcons name="arrow-left" size={22} color="#ffffff" />
      </TouchableOpacity>
      <View style={styles.headerCenter}>
        <Text style={styles.headerTitle}>🎯 Career Guidance</Text>
        <Text style={styles.headerSubtitle}>
          Resources, courses & tools for engineers
        </Text>
      </View>
      <TouchableOpacity 
        style={styles.bookmarkButton}
        onPress={() => navigation.navigate('SavedResources', { savedArticles })}
      >
        <MaterialCommunityIcons name="bookmark" size={22} color="#ffffff" />
      </TouchableOpacity>
    </LinearGradient>
  );

  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <FlatList
        horizontal
        data={tabs}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <TouchableOpacity
            style={[
              styles.tab,
              activeTab === item.id && styles.activeTab,
              { backgroundColor: darkMode ? (activeTab === item.id ? '#4a5568' : '#2d3748') : '#ffffff' }
            ]}
            onPress={() => setActiveTab(item.id)}
          >
            <MaterialCommunityIcons 
              name={item.icon} 
              size={16} 
              color={activeTab === item.id ? '#667eea' : (darkMode ? '#a0aec0' : '#6b7280')} 
            />
            <Text style={[
              styles.tabText,
              { color: activeTab === item.id ? '#667eea' : (darkMode ? '#a0aec0' : '#6b7280') }
            ]}>
              {item.label}
            </Text>
          </TouchableOpacity>
        )}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabsContent}
      />
    </View>
  );

  const renderFieldCard = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.fieldCard,
        { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }
      ]}
      onPress={() => navigation.navigate('FieldDetails', { field: item })}
    >
      <LinearGradient
        colors={[item.color, `${item.color}80`]}
        style={styles.fieldIconContainer}
      >
        <MaterialCommunityIcons name={item.icon} size={24} color="#ffffff" />
      </LinearGradient>
      
      <View style={styles.fieldContent}>
        <Text style={[styles.fieldName, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
          {item.name}
        </Text>
        <Text style={[styles.fieldDescription, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          {item.description}
        </Text>
        
        <View style={styles.fieldStats}>
          <View style={styles.stat}>
            <MaterialCommunityIcons name="trending-up" size={12} color="#10b981" />
            <Text style={[styles.statText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {item.demand}
            </Text>
          </View>
          <View style={styles.stat}>
            <MaterialCommunityIcons name="currency-usd" size={12} color="#f59e0b" />
            <Text style={[styles.statText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {item.avgSalary}
            </Text>
          </View>
          <View style={styles.stat}>
            <MaterialCommunityIcons name="chart-line" size={12} color="#667eea" />
            <Text style={[styles.statText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {item.growth}
            </Text>
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );

  const renderResourceCard = ({ item }) => {
    const isSaved = savedArticles.includes(item.id);
    
    return (
      <TouchableOpacity
        style={[
          styles.resourceCard,
          { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }
        ]}
        onPress={() => openLink(item.url)}
        activeOpacity={0.8}
      >
        <View style={styles.resourceHeader}>
          <View style={styles.resourceType}>
            <MaterialCommunityIcons 
              name={item.icon} 
              size={16} 
              color={darkMode ? '#a0aec0' : '#6b7280'} 
            />
            <Text style={[styles.resourceTypeText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {String(item.type ?? '').toUpperCase()}
            </Text>
          </View>
          
          <TouchableOpacity 
            style={styles.resourceSaveButton}
            onPress={() => toggleSaveArticle(item.id)}
          >
            <MaterialCommunityIcons 
              name={isSaved ? "bookmark" : "bookmark-outline"} 
              size={18} 
              color={isSaved ? "#667eea" : (darkMode ? "#a0aec0" : "#6b7280")} 
            />
          </TouchableOpacity>
        </View>
        
        <Text style={[styles.resourceTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
          {item.title}
        </Text>
        
        <Text style={[styles.resourceDescription, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          {item.description}
        </Text>
        
        <View style={styles.resourceFooter}>
          <View style={styles.resourceMeta}>
            <View style={styles.categoryBadge}>
              <Text style={styles.categoryText}>{item.category}</Text>
            </View>
            <View style={styles.durationBadge}>
              <MaterialCommunityIcons name="clock-outline" size={10} color="#6b7280" />
              <Text style={styles.durationText}>{item.duration}</Text>
            </View>
          </View>
          
          <TouchableOpacity 
            style={styles.shareButton}
            onPress={() => shareResource(item)}
          >
            <MaterialCommunityIcons name="share-variant" size={16} color="#667eea" />
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  const renderCourseCard = ({ item }) => {
    const isCompleted = completedCourses.includes(item.id);
    
    return (
      <TouchableOpacity
        style={[
          styles.courseCard,
          { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }
        ]}
        onPress={() => openLink(item.url)}
        activeOpacity={0.8}
      >
        <View style={styles.courseHeader}>
          <View style={styles.coursePlatform}>
            <MaterialCommunityIcons name="school" size={16} color="#667eea" />
            <Text style={[styles.coursePlatformText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {item.platform}
            </Text>
          </View>
          
          <TouchableOpacity 
            style={styles.courseCompleteButton}
            onPress={() => markCourseComplete(item.id)}
          >
            <MaterialCommunityIcons 
              name={isCompleted ? "check-circle" : "circle-outline"} 
              size={20} 
              color={isCompleted ? "#10b981" : (darkMode ? "#a0aec0" : "#6b7280")} 
            />
          </TouchableOpacity>
        </View>
        
        <Text style={[styles.courseTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
          {item.title}
        </Text>
        
        <Text style={[styles.courseProvider, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          by {item.provider}
        </Text>
        
        <View style={styles.courseDetails}>
          <View style={styles.courseDetailItem}>
            <MaterialCommunityIcons name="clock-outline" size={12} color={darkMode ? '#a0aec0' : '#6b7280'} />
            <Text style={[styles.courseDetailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {item.duration}
            </Text>
          </View>
          
          <View style={styles.courseDetailItem}>
            <MaterialCommunityIcons name="chart-bar" size={12} color={darkMode ? '#a0aec0' : '#6b7280'} />
            <Text style={[styles.courseDetailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {item.level}
            </Text>
          </View>
          
          <View style={styles.courseDetailItem}>
            <MaterialCommunityIcons name="star" size={12} color="#f59e0b" />
            <Text style={[styles.courseDetailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {item.rating}
            </Text>
          </View>
        </View>
        
        <View style={styles.courseFooter}>
          <View style={styles.coursePrice}>
            <Text style={[
              styles.priceText,
              { color: item.free ? '#10b981' : '#667eea' }
            ]}>
              {item.price}
            </Text>
            {item.certificate && (
              <View style={styles.certificateBadge}>
                <MaterialCommunityIcons name="certificate" size={10} color="#ffffff" />
                <Text style={styles.certificateText}>Certificate</Text>
              </View>
            )}
          </View>
          
          <View style={styles.studentsBadge}>
            <MaterialCommunityIcons name="account-group" size={12} color="#6b7280" />
            <Text style={styles.studentsText}>{item.students}</Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderToolCard = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.toolCard,
        { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }
      ]}
      onPress={() => navigation.navigate(item.screen)}
      activeOpacity={0.8}
    >
      <LinearGradient
        colors={['#667eea', '#764ba2']}
        style={styles.toolIconContainer}
      >
        <MaterialCommunityIcons name={item.icon} size={24} color="#ffffff" />
      </LinearGradient>
      
      <View style={styles.toolContent}>
        <Text style={[styles.toolTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
          {item.title}
        </Text>
        <Text style={[styles.toolDescription, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          {item.description}
        </Text>
      </View>
      
      <TouchableOpacity 
        style={styles.toolActionButton}
        onPress={() => navigation.navigate(item.screen)}
      >
        <Text style={styles.toolActionText}>{item.action}</Text>
        <MaterialCommunityIcons name="arrow-right" size={14} color="#667eea" />
      </TouchableOpacity>
    </TouchableOpacity>
  );

  const renderCommunityCard = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.communityCard,
        { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }
      ]}
      onPress={() => openLink(item.url)}
      activeOpacity={0.8}
    >
      <View style={styles.communityHeader}>
        <View style={styles.communityIcon}>
          <MaterialCommunityIcons name={item.icon} size={24} color="#667eea" />
        </View>
        
        <View style={styles.communityInfo}>
          <Text style={[styles.communityTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            {item.title}
          </Text>
          <Text style={[styles.communityType, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            {item.type}
          </Text>
        </View>
      </View>
      
      <Text style={[styles.communityDescription, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        {item.description}
      </Text>
      
      <View style={styles.communityFooter}>
        <View style={styles.membersBadge}>
          <MaterialCommunityIcons name="account-group" size={12} color="#6b7280" />
          <Text style={styles.membersText}>{item.members}</Text>
        </View>
        
        <TouchableOpacity 
          style={styles.visitButton}
          onPress={() => openLink(item.url)}
        >
          <Text style={styles.visitButtonText}>Visit</Text>
          <MaterialCommunityIcons name="open-in-new" size={12} color="#667eea" />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'resources':
        return (
          <FlatList
            data={learningResources}
            keyExtractor={(item) => item.id}
            renderItem={renderResourceCard}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
            ListHeaderComponent={
              <View style={styles.tabHeader}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Learning Resources
                </Text>
                <Text style={[styles.sectionSubtitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  Guides, articles, and videos for career growth
                </Text>
              </View>
            }
          />
        );
      
      case 'courses':
        return (
          <FlatList
            data={onlineCourses}
            keyExtractor={(item) => item.id}
            renderItem={renderCourseCard}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
            ListHeaderComponent={
              <View style={styles.tabHeader}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Online Courses
                </Text>
                <Text style={[styles.sectionSubtitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  Learn new skills from top platforms
                </Text>
              </View>
            }
          />
        );
      
      case 'tools':
        return (
          <FlatList
            data={careerTools}
            keyExtractor={(item) => item.id}
            renderItem={renderToolCard}
            contentContainerStyle={styles.listContent}
            numColumns={2}
            columnWrapperStyle={styles.toolsGrid}
            showsVerticalScrollIndicator={false}
            ListHeaderComponent={
              <View style={styles.tabHeader}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Career Tools
                </Text>
                <Text style={[styles.sectionSubtitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  Tools to enhance your career journey
                </Text>
              </View>
            }
          />
        );
      
      case 'community':
        return (
          <FlatList
            data={communityResources}
            keyExtractor={(item) => item.id}
            renderItem={renderCommunityCard}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
            ListHeaderComponent={
              <View style={styles.tabHeader}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Community & Networks
                </Text>
                <Text style={[styles.sectionSubtitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  Connect with engineering communities
                </Text>
              </View>
            }
          />
        );
      
      default:
        return null;
    }
  };

  const renderMainContent = () => {
    const renderItem = ({ item }) => {
      if (item.type === 'fields') {
        return (
          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
              Engineering Fields
            </Text>
            <Text style={[styles.sectionSubtitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              Explore different engineering specializations
            </Text>
            
            <FlatList
              ref={fieldsListRef}
              horizontal
              data={engineeringFields}
              keyExtractor={(item) => item.id}
              renderItem={renderFieldCard}
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.fieldsList}
            />
          </View>
        );
      }
      
      if (item.type === 'tabs') {
        return (
          <View style={styles.tabsSection}>
            {renderTabs()}
          </View>
        );
      }
      
      if (item.type === 'tabContent') {
        return (
          <View style={styles.tabContentSection}>
            {renderTabContent()}
          </View>
        );
      }
      
      if (item.type === 'stats') {
        return (
          <View style={[styles.statsCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
            <Text style={[styles.statsTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
              Your Career Progress
            </Text>
            
            <View style={styles.statsGrid}>
              <View style={styles.statItem}>
                <MaterialCommunityIcons name="bookmark" size={20} color="#667eea" />
                <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  {savedArticles.length}
                </Text>
                <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  Saved Resources
                </Text>
              </View>
              
              <View style={styles.statItem}>
                <MaterialCommunityIcons name="check-circle" size={20} color="#10b981" />
                <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  {completedCourses.length}
                </Text>
                <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  Completed Courses
                </Text>
              </View>
              
              <View style={styles.statItem}>
                <MaterialCommunityIcons name="toolbox" size={20} color="#f59e0b" />
                <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  {careerTools.length}
                </Text>
                <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  Available Tools
                </Text>
              </View>
              
              <View style={styles.statItem}>
                <MaterialCommunityIcons name="account-group" size={20} color="#8b5cf6" />
                <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  {communityResources.length}
                </Text>
                <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  Communities
                </Text>
              </View>
            </View>
          </View>
        );
      }
      
      if (item.type === 'tips') {
        return (
          <View style={[styles.tipsCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
            <View style={styles.tipsHeader}>
              <MaterialCommunityIcons name="lightbulb-on" size={20} color="#f59e0b" />
              <Text style={[styles.tipsTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                Career Tips
              </Text>
            </View>
            
            <View style={styles.tipItem}>
              <MaterialCommunityIcons name="check-circle" size={14} color="#10b981" />
              <Text style={[styles.tipText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Build a strong portfolio with real projects
              </Text>
            </View>
            
            <View style={styles.tipItem}>
              <MaterialCommunityIcons name="check-circle" size={14} color="#10b981" />
              <Text style={[styles.tipText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Network with professionals in your field
              </Text>
            </View>
            
            <View style={styles.tipItem}>
              <MaterialCommunityIcons name="check-circle" size={14} color="#10b981" />
              <Text style={[styles.tipText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Continuously learn new technologies
              </Text>
            </View>
            
            <View style={styles.tipItem}>
              <MaterialCommunityIcons name="check-circle" size={14} color="#10b981" />
              <Text style={[styles.tipText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Attend industry conferences and events
              </Text>
            </View>
          </View>
        );
      }
      
      return null;
    };

    const mainData = [
      { id: 'fields', type: 'fields' },
      { id: 'tabs', type: 'tabs' },
      { id: 'tabContent', type: 'tabContent' },
      { id: 'stats', type: 'stats' },
      { id: 'tips', type: 'tips' },
    ];

    return (
      <FlatList
        ref={mainListRef}
        data={mainData}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#667eea']}
            tintColor="#667eea"
          />
        }
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.mainListContent}
      />
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.loadingContainer, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
        <ActivityIndicator size="large" color="#667eea" />
        <Text style={[styles.loadingText, { color: darkMode ? '#cbd5e0' : '#6b7280' }]}>
          Loading career guidance...
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
      <StatusBar barStyle={darkMode ? "light-content" : "light-content"} backgroundColor="#667eea" />
      
      {renderHeader()}
      {renderMainContent()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backButton: {
    padding: 6,
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  headerSubtitle: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  bookmarkButton: {
    padding: 6,
  },
  mainListContent: {
    paddingBottom: 20,
  },
  section: {
    paddingHorizontal: 16,
    marginTop: 12,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 13,
    marginBottom: 12,
  },
  fieldsList: {
    paddingRight: 16,
  },
  fieldCard: {
    width: 280,
    marginRight: 12,
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  fieldIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  fieldContent: {
    flex: 1,
  },
  fieldName: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 6,
  },
  fieldDescription: {
    fontSize: 12,
    marginBottom: 12,
    lineHeight: 16,
  },
  fieldStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  stat: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statText: {
    fontSize: 10,
    marginLeft: 4,
  },
  tabsSection: {
    marginHorizontal: 16,
    marginBottom: 16,
  },
  tabsContainer: {
    marginBottom: 0,
  },
  tabsContent: {
    paddingRight: 16,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 2,
    elevation: 1,
  },
  activeTab: {
    borderWidth: 1,
    borderColor: '#667eea',
  },
  tabText: {
    fontSize: 13,
    fontWeight: '500',
    marginLeft: 6,
  },
  tabContentSection: {
    minHeight: 400,
    marginBottom: 20,
  },
  tabHeader: {
    paddingHorizontal: 16,
    marginBottom: 16,
  },
  listContent: {
    paddingBottom: 16,
  },
  resourceCard: {
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    marginHorizontal: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  resourceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  resourceType: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  resourceTypeText: {
    fontSize: 10,
    fontWeight: '500',
    marginLeft: 4,
  },
  resourceSaveButton: {
    padding: 4,
  },
  resourceTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    marginBottom: 8,
    lineHeight: 20,
  },
  resourceDescription: {
    fontSize: 13,
    marginBottom: 12,
    lineHeight: 18,
  },
  resourceFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  resourceMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  categoryBadge: {
    backgroundColor: '#e5e7eb',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  categoryText: {
    fontSize: 10,
    fontWeight: '500',
    color: '#6b7280',
  },
  durationBadge: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  durationText: {
    fontSize: 10,
    color: '#6b7280',
    marginLeft: 2,
  },
  shareButton: {
    padding: 4,
  },
  courseCard: {
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    marginHorizontal: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  courseHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  coursePlatform: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  coursePlatformText: {
    fontSize: 12,
    fontWeight: '500',
    marginLeft: 4,
  },
  courseCompleteButton: {
    padding: 4,
  },
  courseTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    marginBottom: 4,
    lineHeight: 20,
  },
  courseProvider: {
    fontSize: 12,
    marginBottom: 12,
  },
  courseDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  courseDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  courseDetailText: {
    fontSize: 11,
    marginLeft: 4,
  },
  courseFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  coursePrice: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  priceText: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  certificateBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    gap: 2,
  },
  certificateText: {
    fontSize: 9,
    color: '#ffffff',
    fontWeight: '500',
  },
  studentsBadge: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  studentsText: {
    fontSize: 11,
    color: '#6b7280',
    marginLeft: 2,
  },
  toolsGrid: {
    justifyContent: 'space-between',
    marginBottom: 12,
    paddingHorizontal: 16,
  },
  toolCard: {
    width: (width - 48) / 2,
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
    alignItems: 'center',
  },
  toolIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  toolContent: {
    alignItems: 'center',
    marginBottom: 12,
  },
  toolTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 6,
    textAlign: 'center',
  },
  toolDescription: {
    fontSize: 11,
    textAlign: 'center',
    lineHeight: 14,
  },
  toolActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#667eea',
  },
  toolActionText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#667eea',
    marginRight: 4,
  },
  communityCard: {
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    marginHorizontal: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  communityHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  communityIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: '#e5e7eb',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  communityInfo: {
    flex: 1,
  },
  communityTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  communityType: {
    fontSize: 12,
    color: '#6b7280',
  },
  communityDescription: {
    fontSize: 13,
    marginBottom: 12,
    lineHeight: 18,
  },
  communityFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  membersBadge: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  membersText: {
    fontSize: 11,
    color: '#6b7280',
    marginLeft: 2,
  },
  visitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#667eea',
  },
  visitButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#667eea',
    marginRight: 4,
  },
  statsCard: {
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  statsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  statItem: {
    width: (width - 64) / 2,
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
  },
  statNumber: {
    fontSize: 18,
    fontWeight: 'bold',
    marginVertical: 4,
  },
  statLabel: {
    fontSize: 11,
    textAlign: 'center',
  },
  tipsCard: {
    marginHorizontal: 16,
    marginBottom: 20,
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  tipsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  tipsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  tipItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  tipText: {
    fontSize: 13,
    marginLeft: 8,
    flex: 1,
    lineHeight: 18,
  },
});

export default CareerGuidance;